import { ReactNode, useId } from 'react'
import { PageControlBarModule } from '../../components/BaseComponents'
import { formatJurisdictionParts } from '../../lib/jurisdiction'

export type TrainingSessionDetailZones = {
	primarySelector: { label: string; controlId: string; children: ReactNode }
	summary: ReactNode
	filtersAndActions: ReactNode
}

type UseTrainingSessionDetailZonesArgs = {
	sessionSelector: ReactNode
	sessionName: string
	sessionStartDate?: string
	jurisdictionCode?: string | null
	userCount: number
	pendingCount: number
	userNameFilterValue: string
	userNameFilterPlaceholder: string
	onUserNameFilterChange: (value: string) => void
	onUserNameFilterKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
	organisationNameFilterValue: string
	organisationNameFilterPlaceholder: string
	onOrganisationNameFilterChange: (value: string) => void
	onOrganisationNameFilterKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
	onEditSessionClick: () => void
	onDeleteSessionClick: () => void
	onUploadUsersClick: () => void
	onAddTrainingUserClick: () => void
	onManageOrganisationsClick: () => void
}

export function useTrainingSessionDetailZones({
	sessionSelector,
	sessionName,
	sessionStartDate,
	jurisdictionCode,
	userCount,
	pendingCount,
	userNameFilterValue,
	userNameFilterPlaceholder,
	onUserNameFilterChange,
	onUserNameFilterKeyDown,
	organisationNameFilterValue,
	organisationNameFilterPlaceholder,
	onOrganisationNameFilterChange,
	onOrganisationNameFilterKeyDown,
	onEditSessionClick,
	onDeleteSessionClick,
	onUploadUsersClick,
	onAddTrainingUserClick,
	onManageOrganisationsClick,
}: UseTrainingSessionDetailZonesArgs): TrainingSessionDetailZones {
	const sessionSelectorId = useId()

	const formatUserCount = () => {
		const total = userCount + pendingCount
		return pendingCount > 0 ? <>
			<span className="text-white">{total}</span> ({pendingCount} pending)
		</> : <span className="text-white">{total}</span>
	}

	const renderJurisdiction = () => {
		const { name, code } = formatJurisdictionParts(jurisdictionCode)
		return code ? <>
			<span className="text-white">{name}</span> <span className="text-charcoal-100">({code})</span>
		</> : <span className="text-white">{name}</span>
	}

	return {
		primarySelector: {
			label: 'Select training session',
			controlId: sessionSelectorId,
			children: sessionSelector,
		},
		summary: (
			<div className="bg-charcoal-600 rounded p-6 text-charcoal-100 text-sm flex flex-wrap justify-between items-start gap-4">
				<div className="space-y-2">
					<div><span>Scheduled to start:</span> <span className="text-white">{sessionStartDate}</span></div>
					<div><span>User count:</span> {formatUserCount()}</div>
					<div><span>Jurisdiction:</span> {renderJurisdiction()}</div>
				</div>
				<PageControlBarModule.Button
					type='Primary'
					text='Edit session'
					icon='fa-regular fa-pen-to-square'
					hasLeadingIcon
					onClick={onEditSessionClick}
					additionalClass='shrink-0'
				/>
			</div>
		),
		filtersAndActions: (
			<>
				{/* Row 1: Search fields (Edit session now lives in the summary box, above) */}
				<div className='flex flex-wrap gap-x-5 gap-y-4 items-end w-full'>
					<PageControlBarModule.Search
						name='userName'
						label='Search by user name'
						placeholder={userNameFilterPlaceholder}
						value={userNameFilterValue}
						onChange={(e) => onUserNameFilterChange(e.target.value)}
						onKeyDown={onUserNameFilterKeyDown}
						className='w-[300px]'
					/>
					<PageControlBarModule.Search
						name='organisationName'
						label='Search by organisation name'
						placeholder={organisationNameFilterPlaceholder}
						value={organisationNameFilterValue}
						onChange={(e) => onOrganisationNameFilterChange(e.target.value)}
						onKeyDown={onOrganisationNameFilterKeyDown}
						className='w-[300px]'
					/>
				</div>

				{/* Row 2: Action buttons */}
				<div className='flex flex-wrap gap-x-5 gap-y-4 items-end w-full'>
					<PageControlBarModule.Button
						type='Primary'
						text='Add training user'
						hasLeadingIcon
						icon='fa-solid fa-plus'
						onClick={onAddTrainingUserClick}
					/>
					<PageControlBarModule.Button
						type='Outlined'
						text='Import users'
						hasLeadingIcon
						icon='fa-solid fa-file-csv'
						onClick={onUploadUsersClick}
					/>
					<PageControlBarModule.Button
						type='Outlined'
						text='Manage organisations'
						hasLeadingIcon
						icon='fa-solid fa-building'
						onClick={onManageOrganisationsClick}
					/>
					<PageControlBarModule.Button
						type='Primary'
						text='Delete session'
						hasLeadingIcon
						icon='fa-solid fa-trash'
						onClick={onDeleteSessionClick}
					/>
				</div>
			</>
		),
	}
}
